<template>
  <header class="weekly-planning-header sticky top-20 z-10 bg-white">
    <!-- Fila principal -->
    <div class="weekly-planning-header__main">
      <div class="weekly-planning-header__left">
        <router-link
          v-if="!isWeeklyWorkMode"
          :to="{ name: 'project.activities' }"
          class="weekly-planning-header__back"
          :aria-label="$t('back')"
        >
          <b-icon pack="fas" icon="arrow-left" size="is-small" />
        </router-link>

        <div class="weekly-planning-header__titles">
          <h1 class="weekly-planning-header__title">
            {{ $t('week_schedule_title') }}
          </h1>
          <p class="weekly-planning-header__subtitle">
            {{ $t('week_schedule_subtitle') }}
          </p>
        </div>
      </div>

      <div class="weekly-planning-header__center">
        <div class="weekly-planning-header__field">
          <span class="weekly-planning-header__field-label">{{ $t('view') }}</span>
          <b-dropdown aria-role="list" position="is-bottom-left" class="weekly-planning-header__view-dropdown">
            <template #trigger>
              <button type="button" class="weekly-planning-header__view-select">
                <b-icon pack="fas" :icon="selectedViewOption.icon" size="is-small" />
                <span>{{ $t(selectedViewOption.labelKey) }}</span>
                <span class="weekly-planning-header__view-select-divider" />
                <b-icon pack="fas" icon="angle-down" size="is-small" />
              </button>
            </template>
            <b-dropdown-item
              v-for="option in viewOptions"
              :key="option.key"
              aria-role="listitem"
              class="weekly-planning-header__view-option"
              :class="{ 'weekly-planning-header__view-option--active': option.key === selectedView }"
              @click="selectView(option.key)"
            >
              <span class="weekly-planning-header__view-option-content">
                <b-icon pack="fas" :icon="option.icon" size="is-small" />
                <span>{{ $t(option.labelKey) }}</span>
              </span>
              <b-icon
                v-if="option.key === selectedView"
                pack="fas"
                icon="check"
                size="is-small"
                class="weekly-planning-header__view-option-check"
              />
            </b-dropdown-item>
          </b-dropdown>
        </div>
<!-- 
        <button type="button" class="weekly-planning-header__link-btn">
          <b-icon pack="fas" icon="link" size="is-small" />
          <span>{{ $t('wbs_budget_link') }}</span>
        </button> -->
      </div>

      <div class="weekly-planning-header__ops-panel">
        <span class="weekly-planning-header__ops-label">{{ $t('current_week_operations') }}</span>

        <div class="weekly-planning-header__ops-row">
          <div
            class="weekly-planning-header__week-nav"
            :class="{ 'weekly-planning-header__week-nav--disabled': !isWeeklyScope }"
          >
            <button
              type="button"
              class="weekly-planning-header__week-arrow"
              :aria-label="$t('previous')"
              :disabled="!isWeeklyScope || !canGoPrevious"
              @click="$emit('previous-week')"
            >
              <b-icon pack="fas" icon="chevron-left" size="is-small" />
            </button>

            <div class="weekly-planning-header__week-content">
              <b-icon pack="fas" icon="calendar" size="is-small" class="weekly-planning-header__week-icon" />
              <div class="weekly-planning-header__week-text">
                <div class="weekly-planning-header__week-label">{{ weekLabel }}</div>
                <div class="weekly-planning-header__week-range">{{ weekRange }}</div>
              </div>
            </div>

            <button
              type="button"
              class="weekly-planning-header__week-arrow"
              :aria-label="$t('next')"
              :disabled="!isWeeklyScope || !canGoNext"
              @click="$emit('next-week')"
            >
              <b-icon pack="fas" icon="chevron-right" size="is-small" />
            </button>
          </div>

          <div class="weekly-planning-header__view-toggle">
            <button
              type="button"
              class="weekly-planning-header__view-toggle-btn"
              :class="{ 'is-active': isWeeklyScope }"
              @click="setSubactivitiesScope('weekly')"
            >
              <b-icon pack="fas" icon="calendar-week" size="is-small" />
              <span>{{ $t('weekly_view') }}</span>
            </button>
            <button
              type="button"
              class="weekly-planning-header__view-toggle-btn"
              :class="{ 'is-active': !isWeeklyScope }"
              @click="setSubactivitiesScope('general')"
            >
              <b-icon pack="fas" icon="globe" size="is-small" />
              <span>{{ $t('general_view') }}</span>
            </button>
          </div>

          <button
            v-if="rescheduleMode && (!isTakeoffProgressMode || isWbsView)"
            type="button"
            class="weekly-planning-header__pending-btn"
            :class="{ 'weekly-planning-header__pending-btn--active': pendingRescheduleViewActive }"
            @click="$emit('open-pending-reschedule')"
          >
            <b-icon pack="fas" icon="thumbtack" size="is-small" />
            <span>{{ $t('weekly_pending_reschedule') }}</span>
            <span v-if="pendingRescheduleCount" class="weekly-planning-header__pending-badge">
              {{ pendingRescheduleCount }}
            </span>
          </button>

          <button
            type="button"
            class="weekly-planning-header__reschedule-btn"
            :class="{ 'weekly-planning-header__reschedule-btn--confirm': rescheduleMode }"
            :disabled="isTakeoffRescheduleDisabled"
            @click="onRescheduleClick"
          >
            <b-icon pack="fas" :icon="rescheduleMode ? 'check' : 'rotate'" size="is-small" />
            <span v-if="rescheduleMode">{{ $t('weekly_confirm_reschedule') }}</span>
            <span v-else>{{ $t('reschedule') }}</span>
            <span v-if="rescheduleCount" class="weekly-planning-header__reschedule-badge">
              {{ rescheduleCount }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Barra edición (reprogramación; oculto en takeoff) -->
    <div
      v-if="rescheduleMode && !isTakeoffProgressMode"
      class="weekly-planning-header__edit-bar"
    >
      <div class="weekly-planning-header__edit-bar-content">
        <b-icon pack="fas" icon="pen" size="is-small" class="weekly-planning-header__edit-bar-icon" />
        <span>
          Modo edición activo en <strong>S01</strong>. Marca con 📌 las que <strong>NO</strong> deban moverse
          y edita lo que necesites (fechas previstas, encargado, cantidad y mediciones programadas).
          Vuelve a pulsar <strong>Confirmar reprogramación a S02</strong> para consolidar.
        </span>
      </div>
      <button type="button" class="weekly-planning-header__cancel-btn" @click="$emit('cancel-reschedule')">
        <b-icon pack="fas" icon="times" size="is-small" />
        <span>{{ $t('cancel') }}</span>
      </button>
    </div>

    <!-- Filtro por disciplina (takeoff, solo vista WBS) -->
    <div
      v-if="isTakeoffProgressMode && isWbsView"
      class="weekly-planning-header__discipline-bar"
    >
      <span class="weekly-planning-header__discipline-bar-label">
        <b-icon pack="fas" icon="user-tag" size="is-small" />
        <span>{{ $t('view_by_discipline') }}:</span>
      </span>

      <button
        type="button"
        class="weekly-planning-header__discipline-chip weekly-planning-header__discipline-chip--outline"
        :class="{ 'weekly-planning-header__discipline-chip--active': selectedDisciplineId == null }"
        @click="selectDiscipline(null)"
      >
        {{ $t('all') }}
      </button>

      <span
        v-if="rescheduleDisciplines.length"
        class="weekly-planning-header__discipline-bar-divider"
        aria-hidden="true"
      />

      <div class="weekly-planning-header__discipline-chips">
        <button
          v-for="discipline in rescheduleDisciplines"
          :key="discipline.id"
          type="button"
          class="weekly-planning-header__discipline-chip weekly-planning-header__discipline-chip--outline"
          :class="{ 'weekly-planning-header__discipline-chip--active': selectedDisciplineId === discipline.id }"
          @click="selectDiscipline(discipline.id)"
        >
          {{ discipline.name }}
        </button>
        <span
          v-if="loadingRescheduleDisciplines"
          class="weekly-planning-header__discipline-loading"
        >
          {{ $t('loading') }}…
        </span>
      </div>

      <button
        v-if="rescheduleMode"
        type="button"
        class="weekly-planning-header__cancel-btn weekly-planning-header__cancel-btn--discipline"
        @click="$emit('cancel-reschedule')"
      >
        <b-icon pack="fas" icon="times" size="is-small" />
        <span>{{ $t('cancel') }}</span>
      </button>
    </div>

    <div
      v-if="rescheduleMode && isTakeoffProgressMode && selectedDiscipline"
      class="weekly-planning-header__discipline-preview"
    >
        <div class="weekly-planning-header__discipline-preview-head">
          <div class="weekly-planning-header__discipline-preview-titles">
            <div class="weekly-planning-header__discipline-preview-title">
              <b-icon pack="fas" icon="clipboard-list" size="is-small" />
              <span>
                {{ $t('weekly_quantities_template') }} — {{ $t('week') }} {{ previewWeekNumber }}
              </span>
            </div>
            <p class="weekly-planning-header__discipline-preview-subtitle">
              (comparativa en vivo: cantidades asignadas a la semana seleccionada vs. FTD programada en Gestión HH)
            </p>
          </div>
          <div class="weekly-planning-header__discipline-preview-weeks">
            <button
              v-for="weekNum in previewWeekOptions"
              :key="`preview-week-${weekNum}`"
              type="button"
              class="weekly-planning-header__discipline-preview-week"
              :class="{ 'is-active': previewWeekNumber === weekNum }"
              @click="previewWeekNumber = weekNum"
            >
              S{{ weekNum }}
            </button>
          </div>
        </div>

        <div class="weekly-planning-header__discipline-preview-table-wrap">
          <table class="weekly-planning-header__discipline-preview-table">
            <thead>
              <tr>
                <th colspan="4" class="weekly-planning-header__discipline-preview-discipline">
                  {{ selectedDiscipline.name }}
                </th>
              </tr>
              <tr>
                <th class="text-left">{{ $t('stage') }}</th>
                <th>{{ $t('ud') }}</th>
                <th class="text-right">FTD program.</th>
                <th class="text-right">Prog. TAG S{{ previewWeekNumber }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!disciplinePreviewRows.length">
                <td colspan="4" class="text-left">{{ $t('no_results') }}</td>
              </tr>
              <tr
                v-for="(row, index) in pagedDisciplinePreviewRows"
                :key="`${selectedDiscipline.id}-${row.id || row.stage}-${index}`"
              >
                <td class="text-left">{{ row.stage }}</td>
                <td>{{ row.unit }}</td>
                <td class="text-right">{{ row.ftdProgrammed }}</td>
                <td
                  class="text-right"
                  :class="{ 'weekly-planning-header__discipline-preview-value--warn': row.progTagHighlight }"
                >
                  {{ row.progTag }}
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="previewPageCount > 1"
            class="weekly-planning-header__discipline-preview-pager"
          >
            <button
              type="button"
              class="weekly-planning-header__discipline-preview-pager-btn"
              :disabled="previewPage <= 1"
              :aria-label="$t('previous')"
              @click="previewPage -= 1"
            >
              <b-icon pack="fas" icon="chevron-left" size="is-small" />
            </button>
            <span class="weekly-planning-header__discipline-preview-pager-label">
              {{ previewPage }} / {{ previewPageCount }}
            </span>
            <button
              type="button"
              class="weekly-planning-header__discipline-preview-pager-btn"
              :disabled="previewPage >= previewPageCount"
              :aria-label="$t('next')"
              @click="previewPage += 1"
            >
              <b-icon pack="fas" icon="chevron-right" size="is-small" />
            </button>
          </div>
        </div>
      </div>

      <!-- <div class="weekly-planning-header__warning-bar">
        <div class="weekly-planning-header__warning-bar-content">
          <b-icon pack="fas" icon="exclamation-triangle" size="is-small" class="weekly-planning-header__warning-bar-icon" />
          <span>14 subactividades en 14 actividades deberían haber finalizado y siguen pendientes.</span>
        </div>
        <button type="button" class="weekly-planning-header__delayed-btn">
          <b-icon pack="fas" icon="thumbtack" size="is-small" />
          <span>Solo atrasadas</span>
        </button>
      </div> -->
  </header>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import EventBus from '~/plugins/bus'

const BOQ_ROW_KIND_DISCIPLINE = 1
const DISCIPLINE_PREVIEW_PAGE_SIZE = 6
const QUANTITY_DECIMALS = 5

function formatQuantityPreview(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return null
  }
  return number.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: QUANTITY_DECIMALS,
  })
}

export default {
  name: 'weekly-planning-header',

  props: {
    selectedView: {
      type: String,
      default: 'wbs',
    },
    viewOptions: {
      type: Array,
      default() {
        return []
      },
    },
    weekLabel: {
      type: String,
      default: '',
    },
    weekRange: {
      type: String,
      default: '',
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    canGoPrevious: {
      type: Boolean,
      default: false,
    },
    canGoNext: {
      type: Boolean,
      default: false,
    },
    rescheduleCount: {
      type: Number,
      default: 0,
    },
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
    pendingRescheduleCount: {
      type: Number,
      default: 0,
    },
    pendingRescheduleViewActive: {
      type: Boolean,
      default: false,
    },
    editedSubactivities: {
      type: Array,
      default: () => [],
    },
    /** 'weekly' filtra por semana; 'general' trae todo (subactividades o actividades según modo). */
    subactivitiesScope: {
      type: String,
      default: 'general',
      validator(value) {
        return ['weekly', 'general'].includes(value)
      },
    },
  },

  emits: [
    'previous-week',
    'next-week',
    'update:selected-view',
    'update:subactivities-scope',
    'enter-reschedule',
    'confirm-reschedule',
    'cancel-reschedule',
    'open-pending-reschedule',
    'update:selected-discipline-id',
  ],

  data() {
    return {
      rescheduleDisciplines: [],
      loadingRescheduleDisciplines: false,
      selectedDisciplineId: null,
      previewWeekNumber: null,
      previewPage: 1,
      /** stageId → { weekNum → delta vs valor guardado de assignments }. */
      takeoffWeekDraftDeltasByStage: {},
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    selectedViewOption() {
      const viewOptions = this.viewOptions.filter((option) => option.component)
      return viewOptions.find((option) => option.key === this.selectedView)
        || viewOptions[0]
        || { key: 'wbs', labelKey: 'wbs_view', icon: 'layer-group' }
    },

    isWeeklyWorkMode() {
      const configs = this.project && this.project.configs
      return !!(configs && configs.progress_type === 'weekly')
    },

    isTakeoffProgressMode() {
      const configs = this.project && this.project.configs
      return !!(configs && configs.progress_type_mode === 'takeoff')
    },

    isWbsView() {
      return this.selectedView === 'wbs'
    },

    isTakeoffRescheduleDisabled() {
      return this.isTakeoffProgressMode && !this.isWbsView
    },

    isWeeklyScope() {
      return this.subactivitiesScope === 'weekly'
    },

    selectedDiscipline() {
      if (this.selectedDisciplineId == null) {
        return null
      }
      return this.rescheduleDisciplines.find((item) => item.id === this.selectedDisciplineId) || null
    },

    /**
     * Preview stages, ambas columnas de la misma semana y sin cálculos de avance:
     * FTD program. = weekly_distribution del stage (API).
     * Prog. TAG = Σ weekly_distributions de sus assignments (API)
     * + delta de inputs aún sin guardar (solo front, new - saved).
     */
    disciplinePreviewRows() {
      const discipline = this.selectedDiscipline
      if (!discipline || !Array.isArray(discipline.stages)) {
        return []
      }
      const baseWeek = this.basePreviewWeekNumber
      const selectedWeek = Number(this.previewWeekNumber) || baseWeek

      return discipline.stages.map((stage) => {
        const unit = stage.unit
          || (stage.measure_unit && (stage.measure_unit.code || stage.measure_unit.name))
          || '—'
        const ftdProgrammed = this.mapValueForWeek(stage.scheduled_quantity_per_week, selectedWeek)
        const backendProgTag = this.mapValueForWeek(stage.prog_tag_per_week, selectedWeek)
        const deltas = this.takeoffWeekDraftDeltasByStage[stage.id] || {}
        const weekDelta = Number(deltas[selectedWeek])
        const baseline = backendProgTag != null ? backendProgTag : 0
        const progTag = Number(
          (baseline + (Number.isFinite(weekDelta) ? weekDelta : 0)).toFixed(QUANTITY_DECIMALS)
        )
        const ftdNumber = ftdProgrammed != null ? Number(ftdProgrammed) : null
        const ftdFormatted = ftdNumber != null && Number.isFinite(ftdNumber)
          ? formatQuantityPreview(ftdNumber)
          : null
        const progFormatted = Number.isFinite(progTag)
          ? formatQuantityPreview(progTag)
          : null

        return {
          id: stage.id,
          stage: stage.name || stage.code || `#${stage.id}`,
          unit,
          ftdProgrammed: ftdFormatted != null ? ftdFormatted : '—',
          progTag: progFormatted != null ? progFormatted : '—',
          progTagHighlight: ftdNumber != null && Number.isFinite(ftdNumber) && progTag > ftdNumber,
        }
      })
    },

    previewPageCount() {
      const total = this.disciplinePreviewRows.length
      if (total <= 0) {
        return 1
      }
      return Math.ceil(total / DISCIPLINE_PREVIEW_PAGE_SIZE)
    },

    pagedDisciplinePreviewRows() {
      const start = (this.previewPage - 1) * DISCIPLINE_PREVIEW_PAGE_SIZE
      return this.disciplinePreviewRows.slice(start, start + DISCIPLINE_PREVIEW_PAGE_SIZE)
    },

    basePreviewWeekNumber() {
      const fromProp = Number(this.weekNumber)
      if (fromProp > 0) {
        return fromProp
      }
      const match = String(this.weekLabel || '').match(/(\d+)/)
      return match ? Number(match[1]) : 1
    },

    previewWeekOptions() {
      const start = this.basePreviewWeekNumber
      return [0, 1, 2, 3, 4, 5].map((offset) => start + offset)
    },
  },

  watch: {
    isTakeoffProgressMode: {
      immediate: true,
      handler(active) {
        if (active) {
          this.loadRescheduleDisciplines()
          return
        }
        this.resetRescheduleDisciplines()
      },
    },
    weekNumber() {
      this.syncPreviewWeekNumber()
      if (this.isTakeoffProgressMode) {
        this.loadRescheduleDisciplines()
      }
    },
    weekStartDate() {
      if (this.isTakeoffProgressMode) {
        this.loadRescheduleDisciplines()
      }
    },
    selectedDisciplineId(value) {
      this.previewPage = 1
      if (value != null) {
        this.syncPreviewWeekNumber()
      }
    },
    previewPageCount(count) {
      if (this.previewPage > count) {
        this.previewPage = count
      }
    },
    rescheduleMode(active) {
      if (!active) {
        this.takeoffWeekDraftDeltasByStage = {}
      }
    },
  },

  created() {
    this._onTakeoffRescheduleWeekDraftsPreview = this.onTakeoffRescheduleWeekDraftsPreview
    this._onTakeoffRescheduleWeekDraftsSaved = this.onTakeoffRescheduleWeekDraftsSaved
    EventBus.$on('takeoffRescheduleWeekDraftsPreview', this._onTakeoffRescheduleWeekDraftsPreview)
    EventBus.$on('takeoffRescheduleWeekDraftsSaved', this._onTakeoffRescheduleWeekDraftsSaved)
  },

  beforeDestroy() {
    EventBus.$off('takeoffRescheduleWeekDraftsPreview', this._onTakeoffRescheduleWeekDraftsPreview)
    EventBus.$off('takeoffRescheduleWeekDraftsSaved', this._onTakeoffRescheduleWeekDraftsSaved)
  },

  methods: {
    selectView(viewKey) {
      const option = this.viewOptions.find((item) => item.key === viewKey)
      if (option && option.routeName) {
        if (this.rescheduleMode) {
          this.$emit('cancel-reschedule')
        }
        this.$router.push({ name: option.routeName })
        return
      }
      if (viewKey === this.selectedView) {
        return
      }
      if (this.rescheduleMode) {
        this.$emit('cancel-reschedule')
      }
      this.$emit('update:selected-view', viewKey)
    },

    setSubactivitiesScope(scope) {
      if (scope === this.subactivitiesScope) {
        return
      }
      this.$emit('update:subactivities-scope', scope)
    },

    onTakeoffRescheduleWeekDraftsPreview(payload) {
      if (!payload || payload.clear) {
        this.takeoffWeekDraftDeltasByStage = {}
        return
      }
      this.takeoffWeekDraftDeltasByStage = payload.deltaByStage && typeof payload.deltaByStage === 'object'
        ? { ...payload.deltaByStage }
        : {}
    },

    onTakeoffRescheduleWeekDraftsSaved() {
      this.takeoffWeekDraftDeltasByStage = {}
      if (this.isTakeoffProgressMode) {
        this.loadRescheduleDisciplines()
      }
    },

    onRescheduleClick() {
      if (this.isTakeoffRescheduleDisabled) {
        return
      }
      if (this.rescheduleMode) {
        this.$emit('confirm-reschedule')
      } else {
        this.$emit('enter-reschedule')
      }
    },

    selectDiscipline(disciplineId) {
      this.selectedDisciplineId = disciplineId
      this.$emit('update:selected-discipline-id', disciplineId)
      if (disciplineId != null) {
        this.syncPreviewWeekNumber()
      }
    },

    syncPreviewWeekNumber() {
      this.previewWeekNumber = this.basePreviewWeekNumber
    },

    resetRescheduleDisciplines() {
      this.rescheduleDisciplines = []
      this.loadingRescheduleDisciplines = false
      this.selectedDisciplineId = null
      this.previewWeekNumber = null
      this.previewPage = 1
      this.takeoffWeekDraftDeltasByStage = {}
      this.$emit('update:selected-discipline-id', null)
    },

    mapValueForWeek(source, weekNum) {
      if (!source || typeof source !== 'object') {
        return null
      }
      const raw = source[weekNum] != null ? source[weekNum] : source[String(weekNum)]
      if (raw == null || raw === '') {
        return null
      }
      const value = Number(raw)
      return Number.isFinite(value) ? value : null
    },

    scheduledQuantityForWeek(stage, weekNum) {
      return this.mapValueForWeek(stage && stage.scheduled_quantity_per_week, weekNum)
    },

    async loadRescheduleDisciplines() {
      const projectId = this.project && this.project.id
      if (!projectId) {
        this.rescheduleDisciplines = []
        return
      }

      this.loadingRescheduleDisciplines = true
      try {
        const { data } = await axios.get(`/api/v2/project/${projectId}/boq-roots`, {
          params: {
            only_takeoff: 1,
            with_stages: 1,
            with_takeoff_scheduled_quantity: 1,
            current_week_number: this.weekNumber,
            current_week_start: this.weekStartDate || null,
            current_week_end: this.weekEndDate || null,
          },
        })
        const list = (data && data.success && data.boq_roots) ? data.boq_roots : []
        this.rescheduleDisciplines = list
          .filter((node) => Number(node.row_kind) === BOQ_ROW_KIND_DISCIPLINE)
          .map((node) => ({
            id: node.id,
            name: node.name || node.code || `#${node.id}`,
            stages: Array.isArray(node.stages) ? node.stages : [],
          }))
      } catch (error) {
        this.rescheduleDisciplines = []
      } finally {
        this.loadingRescheduleDisciplines = false
      }
    },
  },
}
</script>

<style scoped>
.weekly-planning-header__main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  padding: 0.625rem 1rem;
  border-bottom: 1px solid #c5d4eb;
}

.weekly-planning-header__left {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
  flex: 1 1 220px;
}

.weekly-planning-header__back {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  color: #6b7280;
  background: #fff;
}

.weekly-planning-header__back:hover {
  background: #f9fafb;
}

.weekly-planning-header__titles {
  min-width: 0;
}

.weekly-planning-header__title {
  margin: 0;
  font-family: Raleway, sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  color: #3b66bc;
}

.weekly-planning-header__subtitle {
  margin: 0.125rem 0 0;
  font-family: Raleway, sans-serif;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.3;
  color: #6b7280;
}

.weekly-planning-header__center {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.5rem;
}

.weekly-planning-header__field {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.weekly-planning-header__field-label {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.weekly-planning-header__view-select {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.875rem;
  padding: 0 0.5rem;
  border: 0;
  border-radius: 0.25rem;
  background: #3b66bc;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-planning-header__view-select-divider {
  width: 1px;
  height: 0.875rem;
  margin: 0 0.125rem;
  background: rgba(255, 255, 255, 0.45);
}

.weekly-planning-header__view-dropdown >>> .dropdown-menu {
  min-width: 12rem;
  padding: 0.25rem 0;
}

.weekly-planning-header__view-option >>> a.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3b66bc;
}

.weekly-planning-header__view-option--active >>> a.dropdown-item,
.weekly-planning-header__view-option >>> a.dropdown-item:hover {
  background-color: #eef3fb;
}

.weekly-planning-header__view-option-content {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.weekly-planning-header__view-option-check {
  color: #3b66bc;
}

.weekly-planning-header__link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.875rem;
  padding: 0 0.625rem;
  border: 1px solid #3b66bc;
  border-radius: 0.25rem;
  background: #fff;
  color: #3b66bc;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-planning-header__link-btn:hover {
  background: #f0f5ff;
}

.weekly-planning-header__ops-panel {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: auto;
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  background: #eef3fb;
  border: 1px solid #dbe6f5;
}

.weekly-planning-header__ops-label {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #3b66bc;
}

.weekly-planning-header__ops-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
}

.weekly-planning-header__week-nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 9.5rem;
  padding: 0.125rem 0.375rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background: #fff;
}

.weekly-planning-header__week-nav--disabled {
  opacity: 0.45;
}

.weekly-planning-header__week-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3b66bc;
  cursor: pointer;
}

.weekly-planning-header__week-arrow:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.weekly-planning-header__week-content {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-width: 0;
}

.weekly-planning-header__week-icon {
  flex-shrink: 0;
  color: #3b66bc;
}

.weekly-planning-header__week-text {
  min-width: 0;
  text-align: center;
  line-height: 1.15;
}

.weekly-planning-header__week-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3b66bc;
  white-space: nowrap;
}

.weekly-planning-header__week-range {
  font-size: 0.6875rem;
  font-weight: 400;
  color: #6b7280;
  white-space: nowrap;
}

.weekly-planning-header__view-toggle {
  display: inline-flex;
  overflow: hidden;
  border: 1px solid #3b66bc;
  border-radius: 0.25rem;
}

.weekly-planning-header__view-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  height: 1.875rem;
  padding: 0 0.5rem;
  border: 0;
  background: #fff;
  color: #3b66bc;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-planning-header__view-toggle-btn + .weekly-planning-header__view-toggle-btn {
  border-left: 1px solid #3b66bc;
}

.weekly-planning-header__view-toggle-btn.is-active {
  background: #3b66bc;
  color: #fff;
}

.weekly-planning-header__view-toggle-btn:not(.is-active):hover {
  background: #f0f5ff;
}

.weekly-planning-header__pending-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.875rem;
  padding: 0 0.625rem;
  border: 1px solid #d4a574;
  border-radius: 0.25rem;
  background: #fef9c3;
  color: #92400e;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.weekly-planning-header__pending-btn:hover {
  background: #fef08a;
}

.weekly-planning-header__pending-btn--active {
  background: #fde68a;
  border-color: #b45309;
  box-shadow: inset 0 0 0 1px #b45309;
}

.weekly-planning-header__pending-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.25rem;
  border-radius: 9999px;
  background: #b45309;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
}

.weekly-planning-header__reschedule-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.875rem;
  padding: 0 0.375rem 0 0.5rem;
  border: 0;
  border-radius: 0.25rem;
  background: #3b66bc;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-planning-header__reschedule-btn:hover:not(:disabled) {
  opacity: 0.92;
}

.weekly-planning-header__reschedule-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.7;
}

.weekly-planning-header__reschedule-btn--confirm {
  background: #2563eb;
}

.weekly-planning-header__edit-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  padding: 0.5rem 1rem;
  background: #e8f4fc;
  border-bottom: 1px solid #b8d9f0;
  font-size: 0.8125rem;
  color: #1e40af;
}

.weekly-planning-header__edit-bar-content {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  line-height: 1.4;
}

.weekly-planning-header__edit-bar-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: #3b66bc;
}

.weekly-planning-header__cancel-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid #93c5fd;
  border-radius: 0.25rem;
  background: #fff;
  color: #1e40af;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.weekly-planning-header__cancel-btn:hover {
  background: #f0f9ff;
}

.weekly-planning-header__cancel-btn--discipline {
  margin-left: auto;
  flex-shrink: 0;
}

.weekly-planning-header__discipline-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.625rem;
  padding: 0.5rem 1rem;
  background: #f4f7f6;
  border-bottom: 1px solid #d7e2de;
}

.weekly-planning-header__discipline-bar-label {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.35rem 0.75rem;
  border-radius: 0.25rem;
  background: #1f6b5c;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

.weekly-planning-header__discipline-bar-divider {
  width: 1px;
  align-self: stretch;
  min-height: 1.5rem;
  background: #c5d4ce;
}

.weekly-planning-header__discipline-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  flex: 1 1 auto;
}

.weekly-planning-header__discipline-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 9999px;
  background: #2d6a3e;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
}

.weekly-planning-header__discipline-chip--outline {
  background: #fff;
  border-color: #c5d4ce;
  color: #1f6b5c;
}

.weekly-planning-header__discipline-chip--outline:hover {
  border-color: #3d8b5a;
  background: #e8f5ec;
}

.weekly-planning-header__discipline-chip--active {
  background: #1f6b5c;
  border-color: #1f6b5c;
  color: #fff;
  box-shadow: none;
}

.weekly-planning-header__discipline-chip--active:hover {
  background: #1f6b5c;
  border-color: #1f6b5c;
  color: #fff;
}

.weekly-planning-header__discipline-loading {
  font-size: 0.75rem;
  color: #6b7280;
}

.weekly-planning-header__discipline-preview {
  padding: 0.75rem 1rem 1rem;
  background: #fff;
  border-bottom: 1px solid #d7e2de;
}

.weekly-planning-header__discipline-preview-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}

.weekly-planning-header__discipline-preview-titles {
  min-width: 0;
  flex: 1 1 240px;
}

.weekly-planning-header__discipline-preview-title {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1f2937;
}

.weekly-planning-header__discipline-preview-subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.6875rem;
  line-height: 1.35;
  color: #9ca3af;
}

.weekly-planning-header__discipline-preview-weeks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.weekly-planning-header__discipline-preview-week {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.75rem;
  padding: 0.25rem 0.55rem;
  border: 1px solid #1f6b5c;
  border-radius: 9999px;
  background: #fff;
  color: #1f6b5c;
  font-size: 0.6875rem;
  font-weight: 700;
  cursor: pointer;
}

.weekly-planning-header__discipline-preview-week.is-active {
  background: #1f6b5c;
  color: #fff;
}

.weekly-planning-header__discipline-preview-table-wrap {
  display: inline-block;
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #d7e2de;
  border-radius: 0.25rem;
  vertical-align: top;
}

.weekly-planning-header__discipline-preview-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-top: 1px solid #e5ebe8;
  background: #f8faf9;
}

.weekly-planning-header__discipline-preview-pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: 1px solid #d7e2de;
  border-radius: 0.2rem;
  background: #fff;
  color: #1f6b5c;
  cursor: pointer;
}

.weekly-planning-header__discipline-preview-pager-btn:hover:not(:disabled) {
  background: #e8f3ef;
  border-color: #1f6b5c;
}

.weekly-planning-header__discipline-preview-pager-btn:disabled {
  color: #9ca3af;
  cursor: default;
}

.weekly-planning-header__discipline-preview-pager-label {
  min-width: 2.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #4b5563;
  text-align: center;
}

.weekly-planning-header__discipline-preview-table {
  width: max-content;
  max-width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
  color: #1f2937;
  table-layout: auto;
}

.weekly-planning-header__discipline-preview-discipline {
  padding: 0.45rem 0.75rem;
  text-align: left;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #fff;
  background: #1f6b5c;
}

.weekly-planning-header__discipline-preview-table thead tr:last-child th {
  padding: 0.4rem 0.75rem;
  font-weight: 600;
  color: #fff;
  background: #3d8b5a;
  text-align: center;
  white-space: nowrap;
}

.weekly-planning-header__discipline-preview-table tbody td {
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
  white-space: nowrap;
}

.weekly-planning-header__discipline-preview-table tbody tr:nth-child(even) td {
  background: #f9fafb;
}

.weekly-planning-header__discipline-preview-table .text-left {
  text-align: left;
}

.weekly-planning-header__discipline-preview-table .text-right {
  text-align: right;
}

.weekly-planning-header__discipline-preview-value--warn {
  color: #c2410c;
  font-weight: 700;
}

.weekly-planning-header__warning-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  padding: 0.5rem 1rem;
  background: #fef9c3;
  border-bottom: 1px solid #fde047;
  font-size: 0.8125rem;
  color: #854d0e;
}

.weekly-planning-header__warning-bar-content {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  line-height: 1.4;
}

.weekly-planning-header__warning-bar-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: #ca8a04;
}

.weekly-planning-header__delayed-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid #fcd34d;
  border-radius: 0.25rem;
  background: #fff;
  color: #854d0e;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.weekly-planning-header__delayed-btn:hover {
  background: #fffbeb;
}

.weekly-planning-header__reschedule-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.375rem;
  height: 1.375rem;
  padding: 0 0.25rem;
  border-radius: 9999px;
  background: #fff;
  color: #3b66bc;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
}

@media (max-width: 1200px) {
  .weekly-planning-header__ops-panel {
    margin-left: 0;
    width: 100%;
  }
}
</style>
